import { useEffect, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CalendarClock, Repeat, Infinity as InfinityIcon } from "lucide-react";

/**
 * iter108 — Reusable frequency + schedule picker (Step 1 of the two-step
 * Add Service flow). Feeds the parent a fully-normalized schedule object:
 *
 *   {
 *     frequency: "biweekly",              // one of FREQUENCIES
 *     start_date: "2026-03-05",           // ISO YYYY-MM-DD
 *     arrival_window: "morning",
 *     end_date: null,                     // ISO or null
 *     continue_until_cancelled: true,
 *     custom_schedule_mode: null,         // only when frequency==='custom'
 *     custom_schedule_value: null,
 *   }
 *
 * Every advanced add-on ships with its OWN schedule, independent from
 * Lawn Mowing. When frequency==='one_time' the recurring block is hidden.
 */
export const ADDON_FREQUENCIES = [
  { key: "one_time",       label: "One Time",         recurring: false },
  { key: "weekly",         label: "Weekly",           recurring: true },
  { key: "biweekly",       label: "Every 2 Weeks",    recurring: true },
  { key: "every_3_weeks",  label: "Every 3 Weeks",    recurring: true },
  { key: "monthly",        label: "Monthly",          recurring: true },
  { key: "every_6_weeks",  label: "Every 6 Weeks",    recurring: true },
  { key: "every_2_months", label: "Every 2 Months",   recurring: true },
  { key: "every_3_months", label: "Every 3 Months",   recurring: true },
  { key: "every_6_months", label: "Every 6 Months",   recurring: true },
  { key: "yearly",         label: "Once Per Year",    recurring: true },
  { key: "custom",         label: "Custom Schedule",  recurring: true },
];

const ARRIVAL_WINDOWS = [
  { key: "morning",   label: "Morning · 7am–11am" },
  { key: "midday",    label: "Midday · 11am–2pm" },
  { key: "afternoon", label: "Afternoon · 2pm–5pm" },
  { key: "evening",   label: "Evening · 5pm–8pm" },
];

const CUSTOM_MODES = [
  { key: "every_x_days",         label: "Every X days" },
  { key: "every_x_weeks",        label: "Every X weeks" },
  { key: "nth_weekday_of_month", label: "Nth weekday of month" },
  { key: "free_text",            label: "Special request" },
];

const WEEKDAY_OPTIONS = [
  { key: "first_monday",    label: "First Monday" },
  { key: "first_tuesday",   label: "First Tuesday" },
  { key: "first_wednesday", label: "First Wednesday" },
  { key: "first_thursday",  label: "First Thursday" },
  { key: "first_friday",    label: "First Friday" },
  { key: "second_monday",   label: "Second Monday" },
  { key: "second_tuesday",  label: "Second Tuesday" },
  { key: "second_wednesday", label: "Second Wednesday" },
  { key: "second_thursday", label: "Second Thursday" },
  { key: "second_friday",   label: "Second Friday" },
  { key: "third_monday",    label: "Third Monday" },
  { key: "third_tuesday",   label: "Third Tuesday" },
  { key: "third_wednesday", label: "Third Wednesday" },
  { key: "third_thursday",  label: "Third Thursday" },
  { key: "third_friday",    label: "Third Friday" },
  { key: "fourth_monday",   label: "Fourth Monday" },
  { key: "fourth_tuesday",  label: "Fourth Tuesday" },
  { key: "fourth_wednesday", label: "Fourth Wednesday" },
  { key: "fourth_thursday", label: "Fourth Thursday" },
  { key: "fourth_friday",   label: "Fourth Friday" },
];

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function FrequencySchedulePicker({ value, onChange, serviceName = "this service", testidPrefix = "sched" }) {
  const [freq, setFreq] = useState(value?.frequency || "one_time");
  const [startDate, setStartDate] = useState(value?.start_date || todayISO());
  const [arrivalWindow, setArrivalWindow] = useState(value?.arrival_window || "morning");
  const [endDate, setEndDate] = useState(value?.end_date || "");
  const [continueUntilCancelled, setContinueUntilCancelled] = useState(value?.continue_until_cancelled ?? true);
  const [customMode, setCustomMode] = useState(value?.custom_schedule_mode || "every_x_days");
  const [customValue, setCustomValue] = useState(value?.custom_schedule_value || "");

  const isRecurring = ADDON_FREQUENCIES.find((f) => f.key === freq)?.recurring;

  // Propagate up on any change
  useEffect(() => {
    onChange?.({
      frequency: freq,
      start_date: startDate,
      arrival_window: arrivalWindow,
      end_date: isRecurring && !continueUntilCancelled ? (endDate || null) : null,
      continue_until_cancelled: !!isRecurring && !!continueUntilCancelled,
      custom_schedule_mode: freq === "custom" ? customMode : null,
      custom_schedule_value: freq === "custom" ? (customValue || null) : null,
    });
  }, [freq, startDate, arrivalWindow, endDate, continueUntilCancelled, customMode, customValue, isRecurring]);

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-picker`}>
      <div>
        <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 flex items-center gap-1">
          <Repeat className="w-3.5 h-3.5" /> How often for {serviceName}?
        </Label>
        <p className="text-[11px] text-gray-500 mt-1">Each service has its own schedule — independent from your lawn mowing cadence.</p>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ADDON_FREQUENCIES.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFreq(f.key)}
              data-testid={`${testidPrefix}-freq-${f.key}`}
              className={`px-3 h-11 rounded-xl text-[13px] font-extrabold border transition text-left ${
                freq === f.key
                  ? "border-[#047857] text-white bg-[#047857] shadow-sm"
                  : "border-gray-200 text-gray-700 bg-white hover:border-emerald-300"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom schedule sub-form */}
      {freq === "custom" && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-3" data-testid={`${testidPrefix}-custom-block`}>
          <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-[#047857]">Custom schedule</div>
          <div className="grid grid-cols-2 gap-2">
            {CUSTOM_MODES.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => { setCustomMode(m.key); setCustomValue(""); }}
                data-testid={`${testidPrefix}-custom-${m.key}`}
                className={`px-3 h-10 rounded-lg text-[12px] font-extrabold border transition ${
                  customMode === m.key
                    ? "border-[#047857] text-[#047857] bg-white"
                    : "border-gray-200 text-gray-600 bg-white/60"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {customMode === "every_x_days" && (
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Every ___ days</Label>
              <Input
                type="number" min={1} max={365}
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                placeholder="e.g. 10"
                className="h-11 mt-1 tabular-nums font-extrabold"
                data-testid={`${testidPrefix}-custom-days-input`}
              />
            </div>
          )}
          {customMode === "every_x_weeks" && (
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Every ___ weeks</Label>
              <Input
                type="number" min={1} max={52}
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                placeholder="e.g. 5"
                className="h-11 mt-1 tabular-nums font-extrabold"
                data-testid={`${testidPrefix}-custom-weeks-input`}
              />
            </div>
          )}
          {customMode === "nth_weekday_of_month" && (
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Which weekday?</Label>
              <select
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                className="mt-1 w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-[13px] font-extrabold"
                data-testid={`${testidPrefix}-custom-nth-select`}
              >
                <option value="">Select a day…</option>
                {WEEKDAY_OPTIONS.map((w) => (
                  <option key={w.key} value={w.key}>{w.label}</option>
                ))}
              </select>
            </div>
          )}
          {customMode === "free_text" && (
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Describe your schedule</Label>
              <textarea
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                placeholder="e.g. First warm weekend of each month"
                rows={2}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white p-3 text-[13px]"
                data-testid={`${testidPrefix}-custom-freetext-input`}
              />
              <p className="mt-1 text-[11px] text-emerald-800">Free-text schedules require admin approval before the first visit.</p>
            </div>
          )}
        </div>
      )}

      {/* Recurring schedule fields */}
      {isRecurring && (
        <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4 space-y-3" data-testid={`${testidPrefix}-recurring-block`}>
          <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 flex items-center gap-1">
            <CalendarClock className="w-3.5 h-3.5" /> Schedule details
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-extrabold text-gray-500">Start date</Label>
              <Input
                type="date"
                value={startDate}
                min={todayISO()}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-11 mt-1 font-extrabold"
                data-testid={`${testidPrefix}-start-date`}
              />
            </div>
            <div>
              <Label className="text-[11px] font-extrabold text-gray-500">Arrival window</Label>
              <select
                value={arrivalWindow}
                onChange={(e) => setArrivalWindow(e.target.value)}
                className="mt-1 w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-[13px] font-extrabold"
                data-testid={`${testidPrefix}-arrival-window`}
              >
                {ARRIVAL_WINDOWS.map((w) => (
                  <option key={w.key} value={w.key}>{w.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-xl bg-white border border-gray-200 p-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer" data-testid={`${testidPrefix}-continue-toggle`}>
              <input
                type="checkbox"
                checked={continueUntilCancelled}
                onChange={(e) => setContinueUntilCancelled(e.target.checked)}
                className="w-4 h-4 accent-[#047857]"
              />
              <span className="text-[13px] font-extrabold text-gray-800 flex items-center gap-1.5">
                <InfinityIcon className="w-4 h-4 text-[#047857]" /> Continue until I cancel
              </span>
            </label>
            {!continueUntilCancelled && (
              <div>
                <Label className="text-[11px] font-extrabold text-gray-500">End date</Label>
                <Input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-11 mt-1 font-extrabold"
                  data-testid={`${testidPrefix}-end-date`}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {!isRecurring && (
        <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4" data-testid={`${testidPrefix}-onetime-block`}>
          <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500 flex items-center gap-1">
            <CalendarClock className="w-3.5 h-3.5" /> When to visit
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-extrabold text-gray-500">Preferred date</Label>
              <Input
                type="date"
                value={startDate}
                min={todayISO()}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-11 mt-1 font-extrabold"
                data-testid={`${testidPrefix}-onetime-date`}
              />
            </div>
            <div>
              <Label className="text-[11px] font-extrabold text-gray-500">Arrival window</Label>
              <select
                value={arrivalWindow}
                onChange={(e) => setArrivalWindow(e.target.value)}
                className="mt-1 w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-[13px] font-extrabold"
                data-testid={`${testidPrefix}-onetime-window`}
              >
                {ARRIVAL_WINDOWS.map((w) => (
                  <option key={w.key} value={w.key}>{w.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
